import React, { useCallback, useMemo } from 'react';

import { ActionIcon, useMantineTheme } from '@mantine/core';
import {
  Eventcalendar,
  MbscCalendarEvent,
  MbscCellClickEvent,
  MbscLabelClickEvent,
} from '@mobiscroll/react';
import { IconPlus } from '@tabler/icons-react';
import dayjs from 'dayjs';

import classes from './athleteMonthView.module.css';
import { hasWorkout } from './calendarDay';
import MonthChip from './cards/MonthChip';
import type { AthleteCalendarItem } from './types';

/** Mobiscroll renders its own "N more" counter for the rest. */
const LABELS_PER_CELL = 2;

interface AthleteMonthViewProps {
  date: string;
  itemsByDate: Map<string, AthleteCalendarItem[]>;
  onDaySelect: (date: string) => void;
  onAssignWorkout: (date: string) => void;
  canAssign: boolean;
  calendarMaxDate?: Date;
}

/** A density overview: every click drills into Day, and assigning goes through the `+`. */
const AthleteMonthView = ({
  date,
  itemsByDate,
  onDaySelect,
  onAssignWorkout,
  canAssign,
  calendarMaxDate,
}: AthleteMonthViewProps): JSX.Element => {
  const theme = useMantineTheme();

  const calendarEvents = useMemo(() => {
    const events: MbscCalendarEvent[] = [];
    itemsByDate.forEach((items, day) => {
      items.forEach((item, index) => {
        events.push({
          id: `${day}-${item.kind}-${item.id}`,
          // Every item is all-day at the same instant, so Mobiscroll has nothing to sort by.
          title: item.name,
          start: new Date(`${day}T00:00`),
          allDay: true,
          sortIndex: index,
          athleteCalendarItem: item,
        });
      });
    });
    return events;
  }, [itemsByDate]);

  const eventOrder = useCallback(
    (first: MbscCalendarEvent, second: MbscCalendarEvent) =>
      (first.sortIndex as number) - (second.sortIndex as number),
    [],
  );

  const renderLabel = useCallback((data: { original?: MbscCalendarEvent }): JSX.Element => {
    const item = data.original?.athleteCalendarItem as AthleteCalendarItem | undefined;
    if (!item) return <></>;
    return <MonthChip item={item} />;
  }, []);

  const selectDay = useCallback(
    (args: MbscCellClickEvent | MbscLabelClickEvent) => onDaySelect(dayjs(args.date).format('YYYY-MM-DD')),
    [onDaySelect],
  );

  const renderDayContent = useCallback(
    (data: { date: Date }): JSX.Element => {
      const day = dayjs(data.date).format('YYYY-MM-DD');
      if (!canAssign || hasWorkout(itemsByDate.get(day))) return <></>;

      return (
        <ActionIcon
          size='xs'
          variant='subtle'
          className={classes.assign}
          aria-label={`Assign a workout on ${day}`}
          data-cy='athleteMonthAssign'
          onClick={event => {
            event.stopPropagation();
            onAssignWorkout(day);
          }}
        >
          <IconPlus size={14} />
        </ActionIcon>
      );
    },
    [canAssign, itemsByDate, onAssignWorkout],
  );

  return (
    <Eventcalendar
      theme='ios'
      themeVariant='light'
      className={classes.calendar}
      style={{ fontFamily: theme.fontFamily }}
      data={calendarEvents}
      renderLabel={renderLabel}
      showControls={false}
      eventOrder={eventOrder}
      renderDayContent={renderDayContent}
      onCellClick={selectDay}
      onLabelClick={selectDay}
      selectedDate={dayjs(date).toDate()}
      max={calendarMaxDate}
      view={{ calendar: { labels: LABELS_PER_CELL, popover: false } }}
      data-cy='athleteMonthGrid'
    />
  );
};

export default AthleteMonthView;
